import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { libraryService } from '../../services/libraryService';
import { 
  Download, 
  Eye, 
  Calendar, 
  FileText,
  Image,
  Video,
  Music,
  Archive,
  ArrowLeft
} from 'lucide-react';

const ResourceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const fetchResource = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await libraryService.getResource(id);
      // Handle different response structures
      const resourceData = response.data?.data?.resource || response.data?.data || response.data;
      
      // Debug: Log the resource data to see what we're getting
      console.log('Resource data received:', resourceData);
      
      if (resourceData) {
        setResource(resourceData);
      } else {
        setError('Resource data is empty');
      }
    } catch (e) {
      console.error('Error fetching resource:', e);
      setError(e.response?.data?.message || 'Failed to load resource');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchResource();
  }, [id]);

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-8 h-8 text-red-500" />;
      case 'image': return <Image className="w-8 h-8 text-blue-500" />;
      case 'video': return <Video className="w-8 h-8 text-purple-500" />;
      case 'audio': return <Music className="w-8 h-8 text-green-500" />;
      case 'archive': return <Archive className="w-8 h-8 text-orange-500" />;
      default: return <FileText className="w-8 h-8 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = async () => {
    try {
      // Get presigned URL from backend
      const response = await libraryService.downloadResource(id);
      const downloadUrl = response.data?.data?.downloadUrl || response.data?.downloadUrl;
      
      if (downloadUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = response.data?.data?.filename || resource.title;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback: try direct download endpoint
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
        const baseUrl = apiBaseUrl.replace('/api/v1', '');
        const fallbackUrl = `${baseUrl}/api/v1/library/resources/${id}/download`;
        window.open(fallbackUrl, '_blank');
      }
      
      // Refresh resource data to update download count
      fetchResource();
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download resource. Please try again.');
    }
  };


  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchResource}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  if (!resource) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">{resource.title}</h1>
          <p className="text-gray-600 mt-1">Resource Details</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Resource Info */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  {getFileIcon(resource.type)}
                  <div>
                    <CardTitle className="text-xl">{resource.title}</CardTitle>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge variant="outline">{resource.type}</Badge>
                      <Badge variant="secondary">{resource.category}</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">{resource.description}</p>
              
              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-1 mb-1">
                    <Download className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold">{resource.downloadCount || 0}</span>
                  </div>
                  <p className="text-sm text-gray-600">Downloads</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-1 mb-1">
                    <Eye className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold">{resource.viewCount || 0}</span>
                  </div>
                  <p className="text-sm text-gray-600">Views</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-1 mb-1">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="font-semibold">
                      {resource.createdAt ? new Date(resource.createdAt).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      }) : 'N/A'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">Added</p>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Download Card */}
          <Card>
            <CardHeader>
              <CardTitle>Download Resource</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                {getFileIcon(resource.type)}
                <p className="text-sm text-gray-600 mt-2">
                  {resource.fileSize ? formatFileSize(resource.fileSize) : 'Size not available'}
                </p>
              </div>
              
              <Button onClick={handleDownload} className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Download Now
              </Button>
              
              <div className="text-xs text-gray-500 text-center">
                By downloading, you agree to use this resource responsibly
              </div>
            </CardContent>
          </Card>

          {/* Resource Info */}
          <Card>
            <CardHeader>
              <CardTitle>Resource Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Uploaded by:</span>
                <p className="text-sm">
                  {resource.uploader 
                    ? `${resource.uploader.firstName || ''} ${resource.uploader.lastName || ''}`.trim() || resource.uploader.email
                    : resource.uploadedBy?.name || 'Unknown'}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Subject:</span>
                <p className="text-sm">{resource.subject || 'General'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Grade Level:</span>
                <p className="text-sm">{resource.gradeLevel || 'All Levels'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Tags:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {resource.tags?.map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Related Resources */}
          <Card>
            <CardHeader>
              <CardTitle>Related Resources</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500">Related resources will appear here</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResourceDetail;